/**
 * Token Estimator Utility
 * Client-side estimation of token count from plain text.
 * No API calls needed - uses character-based approximation.
 */

export interface TokenEstimate {
    charCount: number;
    wordCount: number;
    lineCount: number;
    estimatedTokens: number;
    status: 'safe' | 'warning' | 'danger';
    statusLabel: string;
    percentage: number; // 0-100 relative to TOKEN_BUDGET
}

// Token budget constants
// Gemini Flash context window: 1,048,576 tokens (input)
// Reference: https://ai.google.dev/gemini-api/docs/tokens
export const TOKEN_BUDGET = 800_000;  // 80% of 1M context window - safe limit for input
const WARN_THRESHOLD = 600_000;       // 60% - start warning at this level

// Characters per token ratios (empirically validated with translation_chunks.md data)
const CHARS_PER_TOKEN_VI = 2.5;    // Vietnamese: ~2.5 chars/token (multi-byte + diacritics)
const CHARS_PER_TOKEN_EN = 4.0;    // English: ~4 chars/token

/**
 * Detect if text is primarily Vietnamese by checking for diacritical marks.
 * Samples the first 500 characters for performance.
 */
export function detectVietnamese(text: string): boolean {
    const viRegex = /[àáảãạăắằẳẵặâấầẩẫậèéẻẽẹêếềểễệìíỉĩịòóỏõọôốồổỗộơớờởỡợùúủũụưứừửữựỳýỷỹỵđĐ]/;
    const sample = text.slice(0, 500);
    return viRegex.test(sample);
}

/**
 * Format a number with thousand separators.
 * e.g. 4355 -> "4,355"
 */
export function formatTokenCount(n: number): string {
    return n.toLocaleString('en-US');
}

/**
 * Estimate the number of tokens in a text string.
 *
 * @param text - The plain text to estimate tokens for
 * @param lang - Language hint: 'vi' for Vietnamese, 'en' for English, 'auto' to detect
 * @returns TokenEstimate with count, status, label, and percentage
 */
export function estimateTokens(
    text: string,
    lang: 'vi' | 'en' | 'auto' = 'auto'
): TokenEstimate {
    if (!text || text.length === 0) {
        return {
            charCount: 0,
            wordCount: 0,
            lineCount: 0,
            estimatedTokens: 0,
            status: 'safe',
            statusLabel: '0 tokens',
            percentage: 0,
        };
    }

    const charCount = text.length;
    const wordCount = text.split(/\s+/).filter(Boolean).length;
    const lineCount = text.split('\n').length;

    // Determine chars-per-token ratio based on language
    let charsPerToken = CHARS_PER_TOKEN_EN;
    if (lang === 'vi' || (lang === 'auto' && detectVietnamese(text))) {
        charsPerToken = CHARS_PER_TOKEN_VI;
    }

    const estimatedTokens = Math.ceil(charCount / charsPerToken);
    const percentage = Math.min(100, Math.round((estimatedTokens / TOKEN_BUDGET) * 100));

    let status: TokenEstimate['status'];
    let statusLabel: string;

    if (estimatedTokens <= WARN_THRESHOLD) {
        status = 'safe';
        statusLabel = `~${formatTokenCount(estimatedTokens)} tokens`;
    } else if (estimatedTokens <= TOKEN_BUDGET) {
        status = 'warning';
        statusLabel = `~${formatTokenCount(estimatedTokens)} tokens`;
    } else {
        status = 'danger';
        statusLabel = `~${formatTokenCount(estimatedTokens)} tokens`;
    }

    return {
        charCount,
        wordCount,
        lineCount,
        estimatedTokens,
        status,
        statusLabel,
        percentage,
    };
}
